import { forwardRef, useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Search, Sparkles, ScanSearch, X, CornerDownLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Hint } from '../ui/tooltip';
import ItemImage from '../items/ItemImage';
import { cn } from '../../lib/utils';

const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];

const AI_EXAMPLES = [
  'brass pendants for a hotel, at least 6 pieces under €300 cost',
  'outdoor wall lights IP44 in black, bought recently',
  'Signature design pieces that were never shown online',
];

function ModeTab({ active, disabled, disabledReason, icon: Icon, children, onClick }) {
  const btn = (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors',
        active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
        disabled && 'cursor-not-allowed opacity-50 hover:text-muted-foreground'
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
  return disabled && disabledReason ? (
    <Hint text={disabledReason}>
      <span tabIndex={0}>{btn}</span>
    </Hint>
  ) : (
    btn
  );
}

const TextSearch = forwardRef(function TextSearch({ initial, onSubmit, placeholder, loading, icon: Icon, submitLabel, multiline }, ref) {
  const [text, setText] = useState(initial || '');
  useEffect(() => setText(initial || ''), [initial]);
  const submit = (e) => {
    e?.preventDefault();
    onSubmit(text.trim());
  };
  return (
    <form onSubmit={submit} className="relative flex items-center" data-tour="search-input">
      <Icon className="pointer-events-none absolute left-4 h-5 w-5 text-muted-foreground" />
      <input
        ref={ref}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        className={cn(
          'h-12 w-full rounded-lg border border-input bg-background pl-12 pr-24 sm:pr-36 text-[15px] shadow-sm outline-none transition placeholder:text-muted-foreground/80 focus:border-foreground/30 focus:ring-4 focus:ring-ring/10',
          multiline && 'sm:pr-40'
        )}
        aria-label={placeholder}
      />
      <div className="absolute right-2 flex items-center gap-1">
        {text && (
          <button
            type="button"
            onClick={() => {
              setText('');
              onSubmit('');
            }}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <Button type="submit" size="sm" className="h-8 gap-1.5 rounded-lg" disabled={loading}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CornerDownLeft className="h-3.5 w-3.5" />}
          <span className="hidden sm:inline">{submitLabel}</span>
        </Button>
      </div>
    </form>
  );
});

function VisualDrop({ preview, sourceLabel, onFile, onClear, loading }) {
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const take = (file) => {
    setErr('');
    if (!file) return;
    if (!ACCEPT.includes(file.type)) return setErr('Use a JPG, PNG or WebP image.');
    if (file.size > MAX_BYTES) return setErr('Image is larger than 8 MB.');
    onFile(file);
  };

  // paste an image from the clipboard
  useEffect(() => {
    const onPaste = (e) => {
      const f = [...(e.clipboardData?.files || [])].find((x) => x.type.startsWith('image/'));
      if (f) take(f);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  });

  return (
    <div className="space-y-1.5">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          take(e.dataTransfer.files?.[0]);
        }}
        className={cn(
          'flex min-h-[96px] items-center gap-4 rounded-lg border border-dashed bg-background p-3 transition',
          drag ? 'border-foreground/50 bg-accent/50' : 'border-input'
        )}
      >
        {preview != null ? (
          <>
            <ItemImage src={preview} alt="Search image" className="h-20 w-20 shrink-0 border p-1" rounded="rounded-lg" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {sourceLabel || 'Your image'}
              </div>
              <p className="text-xs text-muted-foreground">Showing problem stock that looks like this. Drop another image to replace it.</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button variant="outline" size="sm" className="h-8" onClick={() => fileRef.current?.click()}>
                Replace
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClear} aria-label="Clear image">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()} className="flex w-full items-center justify-center gap-3 py-3 text-left">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
              <ImagePlus className="h-5 w-5 text-muted-foreground" />
            </span>
            <span>
              <span className="block text-sm font-medium">Drop, paste or pick a photo</span>
              <span className="block text-xs text-muted-foreground">A client mood-board, a competitor product, a site photo… JPG / PNG / WebP, ≤ 8 MB</span>
            </span>
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT.join(',')}
          className="hidden"
          onChange={(e) => {
            take(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

export default function SearchBar({ tab, onTabChange, caps, inputRef, search, ai, visual }) {
  const aiOff = !caps.loading && !caps.ai;
  const visualOff = !caps.loading && !caps.visual;
  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="tablist" data-tour="search-modes" className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-1">
          <ModeTab active={tab === 'search'} icon={Search} onClick={() => onTabChange('search')}>
            Search
          </ModeTab>
          <ModeTab active={tab === 'ai'} icon={Sparkles} onClick={() => onTabChange('ai')} disabled={aiOff} disabledReason="AI search is not configured on the server (no LLM key).">
            Ask AI
          </ModeTab>
          <ModeTab active={tab === 'visual'} icon={ScanSearch} onClick={() => onTabChange('visual')} disabled={visualOff} disabledReason="Visual search is off: the image-embedding layer has not been built yet.">
            Visual
          </ModeTab>
        </div>
        <p className="hidden text-xs text-muted-foreground sm:block">
          Press <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">/</kbd> to search
        </p>
      </div>

      {tab === 'search' && (
        <TextSearch
          ref={inputRef}
          initial={search.value}
          onSubmit={search.onSubmit}
          icon={Search}
          submitLabel="Search"
          placeholder={caps.semantic ? 'Search product, brand, colour, material, style, EAN or vendor code…' : 'Search product, brand, colour, EAN or vendor code…'}
        />
      )}
      {tab === 'ai' && (
        <div className="space-y-2">
          <TextSearch
            ref={inputRef}
            initial={ai.value}
            onSubmit={(t) => t && ai.onSubmit(t)}
            loading={ai.loading}
            icon={Sparkles}
            submitLabel="Ask"
            placeholder="Describe what you need, e.g. brass pendants for a hotel, ≥ 6 pcs, under €300 cost"
            multiline
          />
          {!ai.value && (
            <div className="flex flex-wrap gap-1.5">
              {AI_EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => ai.onSubmit(ex)}
                  className="rounded-full border bg-background px-2.5 py-1 text-xs text-muted-foreground transition hover:border-foreground/30 hover:text-foreground"
                >
                  {ex}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === 'visual' && <VisualDrop {...visual} />}
    </div>
  );
}
